import type { settingTypes } from '@ipfabric/utils';
import { FormikSwitchField } from 'components/FormikSwitchField/FormikSwitchField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

const authenticationType = {
  login: 'login' as const,
  apiToken: 'apiToken' as const,
};

export type VeloCloudFormValues =
  BaseVendorFormValues<settingTypes.VeloCloudApiSettings> &
    settingTypes.ApiTokenAuthentication &
    settingTypes.UsernamePasswordAuthentication & {
      authentication?:
        | typeof authenticationType.login
        | typeof authenticationType.apiToken;
    };

type Props = VendorDialogProps<
  VeloCloudFormValues,
  settingTypes.VeloCloudApiSettings,
  settingTypes.UpdateVeloCloudApiSettings
>;

const resetSecretAttributes = (
  values: VeloCloudFormValues,
): VeloCloudFormValues => {
  return {
    ...values,
    apiToken: '',
    username: '',
    password: '',
  };
};

const BaseVeloCloudForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const { validation, authentication, apiToken } =
    msg.discoverySettings.vendorsApi.veloCloudApiForm;
  const formik = useFormik<VeloCloudFormValues>({
    initialValues: resetSecretAttributes({
      ...props.initialValues,
      authentication: authenticationType.login,
    }),
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable
    validate: (values) => {
      const { required, baseUrlStartsWith } = validation;
      const errors: FormikErrors<VeloCloudFormValues> = {};

      if (!values.baseUrl) {
        errors.baseUrl = required;
      } else if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = baseUrlStartsWith;
      }

      if (values.authentication === authenticationType.login) {
        if (!editMode && !values.username) {
          errors.username = required;
        }
        if (!editMode && !values.password) {
          errors.password = required;
        }
      }
      if (values.authentication === authenticationType.apiToken) {
        if (!editMode && !values.apiToken) {
          errors.apiToken = required;
        }
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit(
      ({ authentication, ...values }: VeloCloudFormValues) => {
        const { username, password, apiToken, ...formattedValues } = {
          ...values,
          ...getAdvancedSettingsDefaultValues(values),
        };

        if (isVendorUpdate(values) && props.dialogType === 'edit') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              username: username || formik.initialValues.username || undefined,
              password: password || formik.initialValues.password || undefined,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              apiToken: apiToken || formik.initialValues.apiToken || undefined,
            });
          }
        } else if (props.dialogType === 'add') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              username,
              password,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              apiToken,
            });
          }
        }
      },
    ),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<VeloCloudFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikSwitchField
        name='authentication'
        label={authentication}
        formik={formik}
        options={[
          {
            label: 'Login',
            value: authenticationType.login,
          },
          {
            label: 'Token',
            value: authenticationType.apiToken,
          },
        ]}
        size='small'
      />
      {formik.values.authentication === authenticationType.login && (
        <FormikVendorApiUsernamePasswordFields
          formik={formik}
          isEditMode={editMode}
        />
      )}
      {formik.values.authentication === authenticationType.apiToken && (
        <FormikTextField
          formik={formik}
          name='apiToken'
          label={apiToken}
          placeholder={editMode ? '(unchanged)' : undefined}
          type='password'
        />
      )}
      <FormikVendorApiBaseUrlField formik={formik} />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const VeloCloudForm = memo(BaseVeloCloudForm);
